const MIN_PITCHES_REQUIRED = 1;
const MIN_SCENARIOS_REQUIRED = 1;

export const REP_INSIGHTS_REQUIREMENTS = {
  MIN_PITCHES_REQUIRED,
  MIN_SCENARIOS_REQUIRED,
};

const isRecent = (dateStr: any): boolean => {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return false;
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 30);
  return date.getTime() >= cutoff.getTime();
};

function getScore(item: any): number {
  return Number(
    item?.overallPercentage ??
    item?.overallScore?.overall_percentange ??
    item?.ai_score?.overall_percentange ??
    item?.overallScore ??
    item?.score ??
    0
  );
}

export interface RepInsightsEligibility {
  pitchScoreCount: number;
  scenarioScoreCount: number;
  validMeetingCount: number;
  validRecordingCount: number;
  meetsMinimumCriteria: boolean;
  shouldShowGenerateButton: boolean;   // first-time generate
  shouldShowRegenerateButton: boolean; // re-generate (backend decides outcome)
}

/**
 * Client-side gating logic that mirrors the backend countValidRecentSessions & meetsInsightsMinimum logic (last 30 days).
 */
export function evaluateRepInsightsEligibility({
  repPitches,
  repScenarios,
  repMeetings = [],
  coachData,
}: {
  repPitches: any[];
  repScenarios: any[];
  repMeetings?: any[];
  coachData: any | null;
}): RepInsightsEligibility {
  const recentPitches = (repPitches || []).filter(
    (p) => isRecent(p.createdAt || p.updatedAt) && getScore(p) > 0
  );
  const recentScenarios = (repScenarios || []).filter(
    (s) => isRecent(s.createdAt || s.updatedAt) && getScore(s) > 0
  );

  const pitchScoreCount = recentPitches.length;
  const scenarioScoreCount = recentScenarios.length;

  let validMeetingCount = 0;
  let validRecordingCount = 0;

  (repMeetings || []).forEach((m) => {
    if (isRecent(m.createdAt || m.updatedAt) && getScore(m) > 0) {
      const isLive = m.legacyMongoId?.startsWith("live-");
      if (isLive) {
        validRecordingCount++;
      } else {
        validMeetingCount++;
      }
    }
  });

  const meetsPitchPractice = pitchScoreCount >= 1 && scenarioScoreCount >= 1;
  const meetsMeetingsRecordings = validMeetingCount >= 1 || validRecordingCount >= 1;
  const meetsMinimumCriteria = meetsPitchPractice || meetsMeetingsRecordings;

  const shouldShowGenerateButton = meetsMinimumCriteria && !coachData;
  const shouldShowRegenerateButton = meetsMinimumCriteria && !!coachData;

  return {
    pitchScoreCount,
    scenarioScoreCount,
    validMeetingCount,
    validRecordingCount,
    meetsMinimumCriteria,
    shouldShowGenerateButton,
    shouldShowRegenerateButton,
  };
}